import { get, post } from './utils'

/**
 * The email test bench API. Super-admin only; the gate is EmailTestBenchPolicy
 * on the server, not the nav entry that leads here.
 *
 * 🔴 THERE IS NO RECIPIENT ON ANY REQUEST IN THIS FILE, AND THERE MUST NEVER BE
 * ONE. A test send goes to `current_user.email`, derived server-side and
 * validated against RecipientAllowList. The server permits exactly `mailer`,
 * `conversation_id` and `topic_id` and has no keyword an address could arrive
 * through, so adding a field here would not "work" -- it would be ignored. The
 * address the page displays comes back FROM the server, in
 * MailerCatalogue.recipient. Never send one, never let the user type one.
 */

export type ContextKind = 'conversation' | 'topic' | 'membership' | 'none' | 'locked'
/**
 * `internal` is NOT a person. It marks the two dev alerts, which hardcode an
 * internal address (devteam@, DEV_EMAIL) and cannot reach a platform user at
 * all. Registry declares it rather than letting inference fall through to its
 * `user` default, which would badge them as user mail -- a wrong answer that
 * looks exactly like a right one.
 */
export type RecipientRole = 'guest' | 'host' | 'user' | 'recipient' | 'derived' | 'admin' | 'internal'

export interface Mailer {
  key: string
  label: string
  group: string
  recipient_role: RecipientRole
  context_kind: ContextKind
  runnable: boolean
  /**
   * Plain English. Present only when `runnable` is false.
   *
   * Rendered by ContextPanel, under its "Not available" heading, and NOWHERE
   * else -- it answers "why can't I fire this?", which is a statement about the
   * buttons it sits above, not a description of the email. The "About this
   * email" section deliberately does not repeat it.
   */
  reason: string | null
  /**
   * What this email is FOR, in a sentence or two -- whatever is worth knowing
   * that the label alone does not carry. Hand-written on the server, and
   * OPTIONAL: most mailers have none, and the page renders nothing at all for
   * those. No placeholder, no empty heading.
   *
   * 🔴 This is why the two apps ship together. The API must be serving
   * `explainer` before an SPA that reads it goes live, or the About section is
   * simply blank on every row.
   */
  explainer: string | null
  /**
   * It renders and it can be test-sent, but no real Out of Hand user can
   * currently receive it.
   *
   * ⚠️ NOT the opposite of "active". 25 of the 48 mailers pass no `template:`,
   * so their sends are indistinguishable in mail_receipts -- `dormant: false`
   * on those means UNKNOWN, not healthy. The server badges only the six it has
   * evidence for; nothing here may infer a seventh.
   *
   * 🔴 Advisory only. A dormant mailer stays `runnable` and stays clickable --
   * rendering everything is the bench's whole value, and it is how a broken
   * template is caught BEFORE its feature is switched back on.
   */
  dormant: boolean
  /** Why it is dormant, in a sentence. Present only when `dormant` is true. */
  dormant_reason: string | null
  /** Registry's own caveats. Shown small, and only when there is one. */
  notes: string | null
}

export interface MailerGroup {
  name: string
  mailers: Mailer[]
}

export interface MailerCatalogue {
  /** The address every send goes to. Derived from current_user, server-side. */
  recipient: string
  environment: string
  available_count: number
  total_count: number
  default_conversation_id: number
  groups: MailerGroup[]
}

export interface ConversationOption {
  id: number
  title: string
  topic_title: string | null
  starts_at: string | null
  guest_count: number
  default: boolean
}

export interface TopicOption {
  id: number
  title: string
  slug: string
  conversation_count: number
}

export interface RunContext {
  conversation?: { id: number; title: string; starts_at: string | null; guest_count: number }
  topic?: { id: number; title: string; slug: string } | null
}

export interface PreviewMessage {
  to: string[]
  from: string
  subject: string
  reply_to: string | null
  template: string | null
  template_slug: string | null
  locale: string | null
  merge_language: string | null
}

export interface MailReceipt {
  id: number
  template: string | null
  subject: string | null
  status: string | null
  email: string | null
  reject_reason: string | null
}

export interface RunResult {
  status: 'previewed' | 'sent' | 'unavailable' | 'failed' | 'refused' | 'unsupported' | 'unknown_mailer'
  mailer?: Mailer
  recipient?: string
  context?: RunContext
  warnings?: string[]
  reasons?: string[]

  /* previewed */
  message?: PreviewMessage
  html?: string
  merge_vars?: Array<{ name: string; content: string }>
  summary?: string

  /* sent */
  marked_subject?: string | null
  would_have_gone_to?: string[]
  receipts?: MailReceipt[]
  /** 🔴 True when a send produced no MailReceipt row. Say so loudly. */
  no_receipt?: boolean
}

/**
 * The repo's api/utils helpers resolve to `{ json, response }` and swallow
 * network errors into the resolved value, so every caller has to decide what an
 * absent `json` means. Here it means "the page cannot say anything true", which
 * is a state worth showing rather than an empty list worth pretending about.
 */
const unwrap = <T>(res: any): T => {
  if (!res || typeof res.json === 'undefined') {
    throw new Error('network')
  }
  return res.json as T
}

class EmailTestBenchApi {
  getMailers(): Promise<MailerCatalogue> {
    return get('/email_test_bench/mailers').then((res) => unwrap<MailerCatalogue>(res))
  }

  getConversations(): Promise<{ conversations: ConversationOption[]; default_conversation_id: number }> {
    return get('/email_test_bench/conversations').then((res) =>
      unwrap<{ conversations: ConversationOption[]; default_conversation_id: number }>(res)
    )
  }

  getTopics(): Promise<{ topics: TopicOption[] }> {
    return get('/email_test_bench/topics').then((res) => unwrap<{ topics: TopicOption[] }>(res))
  }

  /** Renders on screen. Dispatches nothing -- no Mandrill call, no receipt. */
  preview(args: { mailer: string; conversation_id?: number; topic_id?: number }): Promise<RunResult> {
    return post('/email_test_bench/preview', { body: JSON.stringify(args) }).then((res) => unwrap<RunResult>(res))
  }

  /**
   * Sends, synchronously, to the signed-in admin's own address.
   * `args` carries no recipient. See the note at the top of this file.
   */
  send(args: { mailer: string; conversation_id?: number; topic_id?: number }): Promise<RunResult> {
    return post('/email_test_bench/send', { body: JSON.stringify(args) }).then((res) => unwrap<RunResult>(res))
  }
}

export default new EmailTestBenchApi()
